/**
 * UsualOrderPrompt
 * ────────────────
 * Auto-shows when a logged-in customer lands on a venue's menu page AND
 * they have at least one saved standard order for that venue.
 *
 * Renders as a dismissible banner above the menu:
 *   "Welcome back, Sarah — order your usual? [Order again] [Show me]"
 *
 * Stores dismissal in sessionStorage so it doesn't re-pop on every nav.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function UsualOrderPrompt({ user, venueSlug }) {
  const [orders, setOrders] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.id || !venueSlug) return;
    const dismissKey = `usual_dismiss_${user.id}_${venueSlug}`;
    if (sessionStorage.getItem(dismissKey)) { setDismissed(true); return; }
    fetch(`${API}/api/directory/standard-orders/${user.id}?venue_slug=${encodeURIComponent(venueSlug)}`)
      .then((r) => r.json())
      .then((d) => setOrders(d.orders || []))
      .catch(() => setOrders([]));
  }, [user?.id, venueSlug]);

  if (!user?.id || dismissed || orders.length === 0) return null;

  const top = orders[0]; // most-recently-used
  const firstName = (user.name || user.email || "").split(/[\s@]/)[0] || "friend";

  function dismiss() {
    sessionStorage.setItem(`usual_dismiss_${user.id}_${venueSlug}`, "1");
    setDismissed(true);
  }

  return (
    <div
      data-testid="usual-order-prompt"
      className="mx-auto max-w-4xl mb-4 px-4 py-3 rounded-xl border-2 flex items-center gap-3 bg-gradient-to-r from-amber-50 to-emerald-50"
      style={{ borderColor: "var(--tpl-accent, #292524)" }}
    >
      <div
        className="w-10 h-10 rounded-full grid place-items-center text-white shrink-0"
        style={{ background: "var(--tpl-accent, #292524)" }}
      >
        <Sparkles className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-stone-900 text-[14px]">
          Welcome back, {firstName}!
        </p>
        <p className="text-[12px] text-stone-600 truncate">
          Order your usual? <strong>{top.name}</strong> — {top.items.length} item{top.items.length === 1 ? "" : "s"}
        </p>
      </div>
      <Link
        to="/account"
        data-testid="usual-order-prompt-open"
        onClick={() => {
          try {
            import("@/components/AccountHeader").then((m) =>
              m.rememberAccountReturn({
                label: "Menu",
                href:  typeof window !== "undefined"
                        ? window.location.pathname + window.location.search
                        : "/",
              }));
          } catch (_e) { /* no-op */ }
        }}
        className="shrink-0 px-3 py-1.5 rounded-full text-white text-[12px] font-bold hover:opacity-90"
        style={{ background: "var(--tpl-accent, #292524)" }}
      >
        Order again
      </Link>
      <button
        data-testid="usual-order-prompt-dismiss"
        onClick={dismiss}
        className="shrink-0 p-1.5 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
